import { WorldPosition } from './KVApi'

// Earth radius in meters
const EARTH_RADIUS = 6371008.8
const toRad = (deg: number) => (deg * Math.PI) / 180
const toDeg = (rad: number) => (rad * 180) / Math.PI

export type KVCoordinateJSON = {
  lat: number
  long: number
  elevation: number
}

export class KVCoordinate {
  lat: number
  long: number
  elevation: number

  constructor(lat: number, long: number, elevation: number) {
    this.lat = lat
    this.long = long
    this.elevation = elevation
  }

  static fromJSON(json: KVCoordinateJSON): KVCoordinate {
    const position = new KVCoordinate(json.lat, json.long, json.elevation)
    return position
  }

  static toJSON(position: KVCoordinate): KVCoordinateJSON {
    return {
      lat: position.lat,
      long: position.long,
      elevation: position.elevation,
    }
  }

  static fromWorldCoordinate(
    position: WorldPosition,
    origin: KVCoordinate,
    measurementScale: number = 1
  ): KVCoordinate {
    // Flip the y coordinate because increasing y direction is flipped in world coordinates in unreal
    const northing = -position.y * (measurementScale / 100)
    const easting = position.x * (measurementScale / 100)
    const z = position.z * (measurementScale / 100)

    const lat = toRad(origin.lat)
    const lon = toRad(origin.long)

    const dLat = northing / EARTH_RADIUS
    const dLon = easting / (EARTH_RADIUS * Math.cos(lat))

    const lat2 = lat + dLat
    let lon2 = lon + dLon
    lon2 = ((lon2 + Math.PI) % (2 * Math.PI)) - Math.PI

    const height = z + origin.elevation

    return new KVCoordinate(toDeg(lat2), toDeg(lon2), height)
  }

  static toWorldCoordinate(
    coordinate: KVCoordinate,
    origin: KVCoordinate,
    measurementScale: number = 1
  ): WorldPosition {
    const lat1 = toRad(origin.lat)
    const lon1 = toRad(origin.long)

    const lat2 = toRad(coordinate.lat)
    const lon2 = toRad(coordinate.long)

    const dLat = lat2 - lat1
    const dLon = lon2 - lon1

    const northing = dLat * EARTH_RADIUS
    const easting = dLon * EARTH_RADIUS * Math.cos(lat1)

    const x = easting / (measurementScale / 100)
    const y = -northing / (measurementScale / 100)
    const z =
      (coordinate.elevation - origin.elevation) / (measurementScale / 100)

    return { x, y, z }
  }

  static betweenWorlds(
    fromOrigin: KVCoordinate,
    toOrigin: KVCoordinate,
    position: WorldPosition,
    fromMeasurementScale: number,
    toMeasurementScale: number
  ): WorldPosition {
    const latlon = KVCoordinate.fromWorldCoordinate(
      position,
      fromOrigin,
      fromMeasurementScale
    )
    return KVCoordinate.toWorldCoordinate(latlon, toOrigin, toMeasurementScale)
  }
}
